/**
 * Registry of BaaS capability IDs → product + permission requirements.
 * A capability is granted when the instance product is enabled AND the user has the permission.
 * `permission` may be a string or an array (any-of / OR).
 *
 * Master role: `super_user` bypasses product + permission checks (full platform access).
 */

/** Platform master role — highest privilege; overrides permission checkboxes. */
export const BAAS_MASTER_ROLE = "super_user";

/** Instance administrator — full console on linked instances (not platform master). */
export const BAAS_INSTANCE_ADMIN_ROLE = "admin";

const normalizeBaasRole = (roleOrCapabilities) => {
  const role =
    typeof roleOrCapabilities === "string" || roleOrCapabilities == null
      ? roleOrCapabilities
      : roleOrCapabilities?.role;
  return String(role || "")
    .trim()
    .toLowerCase()
    .replace(/-/g, "_");
};

export const isBaasMasterRole = (roleOrCapabilities) => {
  const normalized = normalizeBaasRole(roleOrCapabilities);
  return (
    normalized === BAAS_MASTER_ROLE ||
    normalized === "superuser"
  );
};

/**
 * Full console on the selected instance: platform master or instance `admin`.
 * Mirrors IbPermissionCatalog.IsFullConsoleRole — Contas / accounts.read must not
 * be blocked by partial capability_permissions or missing `entities` feature rows.
 */
export const isBaasFullConsoleRole = (roleOrCapabilities) => {
  if (isBaasMasterRole(roleOrCapabilities)) return true;
  return normalizeBaasRole(roleOrCapabilities) === BAAS_INSTANCE_ADMIN_ROLE;
};

export const BAAS_CAPABILITIES = {
  "entities.create": { product: "entities", permission: "entities.create" },
  "entities.update": {
    product: "entities",
    permission: ["entities.update", "entities.create"],
  },
  "entities.list": { product: "entities", permission: "entities.read" },
  "accounts.view": { product: "entities", permission: "accounts.read" },
  "accounts.create": { product: "entities", permission: ["accounts.create", "entities.create"] },
  "accounts.transfer": { product: "entities", permission: "transactions.write" },
  "issuer.programs.create": { product: "issuer", permission: "issuer.programs.create" },
  "issuer.panel.view": { product: "issuer", permission: "issuer.read" },
  "api.keys.manage": { product: "api", permission: "api.keys.manage" },
  "users.manage": { product: null, permission: "users.manage" },
  "settings.write": { product: null, permission: "console.write" },
  /**
   * Platform "Criar instância" — Valut matrix admin (console.write / instances.create)
   * or master role `super_user` (bypasses via isBaasMasterRole).
   * Never grant from IB hasPermission("admin") alone (often defaulted for Carteira).
   */
  "instances.create": {
    product: null,
    permission: ["instances.create", "console.write"],
  },
  "processing.pix.view": { product: "pix", permission: "transactions.read" },
  /** Create / delete Pix keys — requires write; viewer (visualizar) must not get this. */
  "processing.pix.write": { product: "pix", permission: "transactions.write" },
  "processing.efx.view": { product: "efx", permission: "transactions.read" },
  /** Viewer may use stables.read / accounts.read / transactions.read. */
  "processing.stables.view": {
    product: "stables",
    permission: ["transactions.read", "stables.read", "accounts.read"],
  },
  "stables.operate": { product: "stables", permission: "stables.operate" },
  "stables.wallets.create": { product: "stables", permission: "stables.operate" },
  /** Crédito e garantias is part of Emissor (issuer), not a peer product. */
  "credit.panel.view": { product: "issuer", permission: "credit.read" },
};

/** Capability tokens that imply create / edit / delete / save. */
export const BAAS_WRITE_PERMISSIONS = [
  "entities.create",
  "entities.update",
  "accounts.create",
  "issuer.programs.create",
  "api.keys.manage",
  "users.manage",
  "transactions.write",
  "stables.operate",
  "console.write",
  "instances.create",
];

const permissionList = (rulePermission) => {
  if (Array.isArray(rulePermission)) return rulePermission;
  if (rulePermission == null || rulePermission === "") return [];
  return [rulePermission];
};

export const evaluateCapability = (capabilities, capabilityId) => {
  const rule = BAAS_CAPABILITIES[capabilityId];
  if (!rule || !capabilities) {
    return { allowed: false, blockReason: "permission_denied" };
  }

  // Product gates always apply — including for master / instance admin.
  // What is not enabled on the instance (or denied on the entity) must not appear.
  if (rule.product) {
    const productEnabled = capabilities.products?.[rule.product]?.enabled ?? false;
    if (!productEnabled) {
      return { allowed: false, blockReason: "product_disabled" };
    }
  }

  // Master (super_user) or instance admin: skip permission-token checks only
  // (admin catalog already implies accounts.read / transactions.read etc.).
  if (isBaasFullConsoleRole(capabilities)) {
    return { allowed: true, blockReason: null };
  }

  const granted = capabilities.permissions ?? [];
  const required = permissionList(rule.permission);
  const hasPermission = required.some((permission) => granted.includes(permission));
  if (!hasPermission) {
    return { allowed: false, blockReason: "permission_denied" };
  }

  return { allowed: true, blockReason: null };
};

/**
 * True when the caller has no write/mutate capability tokens (console viewer / visualizador).
 * FE pattern for write CTAs (create / edit / save / delete): **hide** them when this is true
 * or when `can(capabilityId)` is false — do not leave buttons enabled that only 403 on click.
 */
export const isReadOnlyCapabilities = (capabilities) => {
  if (isBaasFullConsoleRole(capabilities)) {
    return false;
  }
  const role = String(capabilities?.role || "").trim().toLowerCase();
  if (role === "viewer" || role === "visualizador" || role === "read") {
    return true;
  }
  const granted = capabilities?.permissions ?? [];
  if (!granted.length) return true;
  return !granted.some((permission) => BAAS_WRITE_PERMISSIONS.includes(permission));
};

/** Alias — prefer this name at org-level gates (instances home / create route). */
export const isBaasConsoleReadOnly = isReadOnlyCapabilities;

/**
 * Correntista / banking rails (Pix, Stables, Fiat, EFX).
 * Cards-only (emissor) instances leave these off — Contas then shows instance
 * shells only (PROGRAM_RESERVE / OPERATING / …), not customer accounts.
 */
export const hasBaasBankingRails = (capabilities) => {
  const products = capabilities?.products || {};
  return (
    products.pix?.enabled === true ||
    products.stables?.enabled === true ||
    products.fiat?.enabled === true ||
    products.efx?.enabled === true
  );
};

/** Issuer / cards without banking rails — Contas shows instance shells only (not full customer accounts). */
export const isBaasCardsOnlyInstance = (capabilities) => {
  const products = capabilities?.products || {};
  const cardsOrIssuer =
    products.cards?.enabled === true || products.issuer?.enabled === true;
  return cardsOrIssuer && !hasBaasBankingRails(capabilities);
};

/**
 * Account-lifecycle CTAs: Criar conta / virtual / reenviar e-mail / consultar proposta.
 * Bloquear / Suspender / Desativar / Ativar are gated separately and hidden on Root embeds.
 *
 * "Liberado / configurado na instância" = banking rails enabled
 * (`hasBaasBankingRails`: pix | stables | fiat | efx). Cards-only instances fail this.
 *
 * Root (`embedded`): Valut config admin only (`isValutConfigAdmin`, already
 * suppressed during view-as). Contas client detail: any caller with
 * `accounts.create` once rails are on.
 */
export const canShowBaasAccountLifecycleActions = ({
  embedded = false,
  isValutConfigAdmin = false,
  capabilities = null,
  canCreateAccount = false,
} = {}) => {
  if (!canCreateAccount) return false;
  if (!hasBaasBankingRails(capabilities)) return false;
  if (embedded) return Boolean(isValutConfigAdmin);
  return true;
};
